import React, { useCallback, useContext, useMemo } from 'react';
import { history, useLocation, useParams } from 'umi';
import { Layout } from 'tea-component';
import NewIconTabs from '@intelligence/components/NewIconTabs';
import { StudioContext } from '@/pages/home/pages/studio/context';
import type { INavStudioGameMenuItem } from '@/pages/home/types/home';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import {
  buildStudioGamePath,
  buildTabChildPath,
  getTabChildPathFromRealPath,
  getStudioTabFromUrl,
  matchStudioTabChildPath,
  parseStudioUrlInfo,
} from '@/pages/home/pages/studio/utils';
import { STUDIO_OPERATION } from '@/pages/home/pages/studio/constant';
import {
  PAGE_TYPE_AUTH_PATH,
  PAGE_TYPE_OUT_PATH,
  PAGE_TYPE_OUT_URL,
} from '@/pages/dashboard/_config/constant';
import AuthPath from '@home/pages/studio/authPath/AuthPathContent';
import StudioState from '@home/pages/studio/components/StudioState';
import FullScreenBtn from '@home/pages/studio/components/FullScreenBtn';
import { getBasePath } from '@/utils/utils';
import {
  getAuthPathIframeHeight,
  type IAuthPathPageConfig,
} from '@home/pages/studio/authPath/configUtils';

const { Content } = Layout;

interface ITabParams {
  studioId: string;
  gameCode: string;
}

interface ITabContainerContentProps {
  children?: React.ReactNode;
  config?: IAuthPathPageConfig['config'];
  /** CommonPageRender 将原有路由 children 放入 data，避免与配置 children 混淆。 */
  data?: {
    routeChildren?: React.ReactNode;
  };
}

const TabContainerContent: React.FC<ITabContainerContentProps> = ({
  children,
  config,
  data,
}) => {
  const { studioId = '', gameCode = '' } = useParams() as unknown as ITabParams;
  const location = useLocation();
  const { hubPath, realPath } = parseStudioUrlInfo(location.pathname);
  const activeTab = getStudioTabFromUrl(location.pathname, location.search) || STUDIO_OPERATION;
  const { tabContainerMenu, currentGame, menuPermission } = useContext(StudioContext);
  const { isEN } = useLocal();

  const tabs = useMemo(() => tabContainerMenu?.tabs ?? [], [tabContainerMenu]);
  const hasRouteChildrenData = Object.prototype.hasOwnProperty.call(
    data ?? {},
    'routeChildren',
  );
  const routeChildren = hasRouteChildrenData ? data?.routeChildren : children;

  const childPathInUrl = useMemo(
    () => getTabChildPathFromRealPath(realPath),
    [realPath],
  );

  const activeTabItem = useMemo(() => {
    if (!childPathInUrl) return tabs[0] ?? null;
    const found = tabs.find((tab: INavStudioGameMenuItem) => (
      matchStudioTabChildPath(tab, childPathInUrl)
    ));
    return found ?? tabs[0] ?? null;
  }, [tabs, childPathInUrl]);

  const activeTabId = activeTabItem?.u_id ?? '';

  const tabItems = useMemo(() => tabs.map((tab: INavStudioGameMenuItem) => ({
    id: tab.u_id,
    label: (
      <span className="dbi-sourcelabel-text">
        {isEN ? (tab.name_en || tab.name_cn) : (tab.name_cn || tab.name_en)}
      </span>
    ),
  })), [tabs, isEN]);

  const handleTabChange = useCallback((tab: { id: string; }) => {
    if (tab.id === activeTabId) return;
    const targetTab = tabs.find(
      (item: INavStudioGameMenuItem) => item.u_id === tab.id,
    );
    if (!targetTab) return;
    if (targetTab.page_type === PAGE_TYPE_OUT_URL) {
      window.open(String(targetTab.url || targetTab.path));
      return;
    }
    if (targetTab.page_type === PAGE_TYPE_OUT_PATH) {
      window.open(`${getBasePath()}${targetTab.url || targetTab.path}`);
      return;
    }
    const targetPath = buildTabChildPath(
      tabContainerMenu?.name_en ?? '',
      targetTab,
    );
    history.push(
      buildStudioGamePath(
        studioId,
        gameCode,
        targetPath,
        activeTab,
        Boolean(hubPath),
      ),
    );
  }, [
    activeTabId,
    tabs,
    tabContainerMenu,
    studioId,
    gameCode,
    activeTab,
    hubPath,
  ]);

  if (!tabs.length) return null;

  if (menuPermission === false) {
    return <StudioState isNoPermission />;
  }

  const renderContent = () => {
    if (!activeTabItem) return <StudioState />;

    if (activeTabItem.page_type === PAGE_TYPE_AUTH_PATH) {
      const iframeHeight = getAuthPathIframeHeight(
        { config },
        activeTabItem.name_en,
      );
      return (
        <AuthPath
          key={activeTabItem.u_id}
          game={currentGame}
          iframeHeight={iframeHeight}
          menu={{
            id: activeTabItem.u_id,
            u_id: activeTabItem.u_id,
            path: activeTabItem.path,
            page_type: PAGE_TYPE_AUTH_PATH,
            name_en: activeTabItem.name_en,
            iframe_height: activeTabItem.iframe_height,
          }}
        />
      );
    }

    return routeChildren ?? <StudioState />;
  };

  return (
    <Content className="dbh-tab-container">
      <Content.Header className="dbh-tab-container__header">
        <NewIconTabs
          ceiling
          tabs={tabItems}
          animated={false}
          activeId={activeTabId}
          onActive={handleTabChange}
          newMarkIdsMappingKey=""
        />
      </Content.Header>
      <Content.Body full className="dbh-tab-container__body">
        {activeTabItem?.page_type === PAGE_TYPE_AUTH_PATH ? (
          <div className="dbh-studio-multi-tab-fullscreen">
            {renderContent()}
          </div>
        ) : (
          <FullScreenBtn
            key={activeTabId}
            className="dbh-studio-multi-tab-fullscreen"
            priority={200}
          >
            {renderContent()}
          </FullScreenBtn>
        )}
      </Content.Body>
    </Content>
  );
};

export default TabContainerContent;
